'use client';

import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import AnimatedHeight from './AnimatedHeight';
import MobileDrawerWithTextAndImage from './MobileDrawerWithTextAndImage';

export type QuoteProps = {
  quoteCompanyLogo: React.ReactNode;
  quoteBody: React.ReactNode;
  quoteAuthor: string;
  quoteAuthorJobTitle: string;
}

export type TextMediaQuoteProps = {
  title: React.ReactNode;
  features: {
    icon: React.ReactNode;
    title: string;
    description: string;
    list?: string[];
    tag?: string;
    image: React.ReactNode;
  }[];
  quote: QuoteProps;
}

const TextMediaQuote: FC<TextMediaQuoteProps> = ({ title, features = [], quote }) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);
  const activeFeature = features[activeFeatureIndex];

  const mobileNavSlider = (
    <div className="flex md:hidden flex-wrap">
      {features.map((feature, index) => (
        <MobileDrawerWithTextAndImage
          key={`text-media-quote-feature-${index}`}
          title={feature.title}
          description={feature.description}
          image={feature.image}
          isActive={index === activeFeatureIndex}
          onClickCallback={() => setFeatureIndex(index)}
          titleTextColor="text-gray-900"
          textColor="text-gray-700"
          iconColor="#0D1D39"
          borderColor="border-gray-200"
        />
      ))}
    </div>
  );

  const desktopNav = (
    <div className="hidden md:flex flex-col gap-2">
      {features.map((feature, index) => (
        <div
          key={`desktop-feature-${feature.title?.replace(/\s/g, '-') ?? index}`}
          className={cx(
            'p-3 flex flex-col gap-2 rounded-20 relative transition-colors duration-400 ease-in-out-cubic',
            {
              'bg-white-100': index === activeFeatureIndex,
              'border-transparent': index !== activeFeatureIndex,
            },
          )}
        >
          <div className="flex flex-col gap-1">
            <div className="flex gap-1 items-center">
              <button
                onClick={() => setFeatureIndex(index)}
                className={`block relative z-1 link-cover text-20/28 font-500 tracking-tight ${index === activeFeatureIndex ? "text-gray-900" : "text-blue-900"}`}
              >
                {feature.title}
              </button>
              {feature.tag && (
                <span className="bg-citron-500 text-citron-900 block rounded-4 px-half text-10/20 h-[20px] uppercase font-mono font-800">
                  {feature.tag}
                </span>
              )}
            </div>
          </div>
          <AnimatedHeight
            isExpanded={index === activeFeatureIndex}
          >
            <div className='text-p2 text-gray-700'>
              {feature.description}
              {feature.list &&
                <ul className='list-disc ps-2 pt-2'>
                  {feature.list.map(item =>
                    <li className="marker:text-blue-900 mb-[10px]" key={item}>{item}</li>
                  )}
                </ul>
              }
            </div>
          </AnimatedHeight>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-white px-container-mobile md:px-container-desktop py-6 md:py-20 relative overflow-hidden">
      <div className="mx-auto max-w-grid">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-9 relative z-1">
          <div className="md:col-span-6 lg:col-span-5 flex flex-col gap-6">
            <div className="flex flex-col gap-3 items-start">
              <h2 className="text-h2 text-blue-900 md:text-h1 font-500 tracking-tight">{title}</h2>
            </div>
            {desktopNav}
          </div>
          <div className="md:col-span-6 lg:col-span-6 lg:col-start-7 flex flex-col gap-4">
            <div className="hidden md:flex relative overflow-hidden w-full aspect-[650/612]">
              <AnimatePresence mode="wait">
                <motion.div
                  className="absolute inset-0"
                  key={`text-media-quote-feature-${activeFeatureIndex}-${activeFeature.title}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  {activeFeature.image}
                </motion.div>
              </AnimatePresence>
            </div>

            {mobileNavSlider}
          </div>
        </div>
        <div className="flex flex-wrap max-w-full justify-center mt-[120px]">
          <div className="flex w-full justify-center">
            {quote.quoteCompanyLogo}
          </div>
          <div className="flex max-w-[1024px] font-500 text-h2 text-center font-serif mt-[30px]">
            {quote.quoteBody}
          </div>
          <div className="flex w-full justify-center items-center mt-[24px]">
            <span className="text-p2 text-gray-700 font-500">{quote.quoteAuthor}</span>
            <span className="uppercase text-gray-700 text-caption1 ml-[20px]">{quote.quoteAuthorJobTitle}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextMediaQuote;
